import React, { ReactNode } from "react";

import { Dialog } from "@packages/ds";

import { Tooltip } from "ds/Tooltip";
import { sendPendoEventDialog } from "Store/analytics/pendoFunctions";

// Types from the design system
type BaseDialogProps = React.ComponentProps<typeof Dialog.Dialog>;
type BaseRootProps = React.ComponentProps<typeof Dialog.Root>;
type BaseTriggerProps = React.ComponentProps<typeof Dialog.Trigger>;
type BaseContentProps = React.ComponentProps<typeof Dialog.Content>;

// Tracked version of the main Dialog component
export interface TrackedDialogProps
  extends Omit<BaseDialogProps, "onOpenChange" | "onHoverChange"> {
  analyticId?: string;
  tooltip?: ReactNode;
  tag?: ReactNode;
}

export const TrackedDialog = ({
  analyticId,
  tooltip,
  tag,
  ...props
}: TrackedDialogProps) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (analyticId) {
      sendPendoEventDialog({ isOpen, analyticId });
    }
  };

  return (
    <Dialog.Dialog
      {...props}
      onOpenChange={handleOpenChange}
      tooltip={
        tooltip && tag ? (
          <Tooltip
            align="end"
            pointerPosition="fromRight=0px"
            tooltip={tooltip}
            alignOffset={15}
          >
            {tag}
          </Tooltip>
        ) : undefined
      }
      tag={tag}
    />
  );
};

// Tracked Root component
export interface TrackedDialogRootProps extends BaseRootProps {
  analyticId?: string;
}

export const TrackedDialogRoot = ({
  analyticId,
  onOpenChange,
  ...props
}: TrackedDialogRootProps) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (analyticId) {
      sendPendoEventDialog({ isOpen, analyticId });
    }
    onOpenChange?.(isOpen);
  };

  return <Dialog.Root {...props} onOpenChange={handleOpenChange} />;
};

// Tracked Trigger component
export interface TrackedDialogTriggerProps extends BaseTriggerProps {
  analyticId?: string;
}

export const TrackedDialogTrigger = ({
  analyticId,
  onHoverChange,
  children,
  ...props
}: TrackedDialogTriggerProps) => {
  const handleHoverChange = (isHovered: boolean) => {
    if (isHovered && analyticId) {
      sendPendoEventDialog({ isOpen: isHovered, analyticId });
    }
    onHoverChange?.(isHovered);
  };

  return (
    <Dialog.Trigger {...props} onHoverChange={handleHoverChange}>
      {children}
    </Dialog.Trigger>
  );
};

// Tracked Content component - just a pass-through
export type TrackedDialogContentProps = BaseContentProps;
export const TrackedDialogContent = Dialog.Content;
